import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";

import PartnerModal from "@client/components/containers/PartnerModal";
import FilterListItem from "@client/components/modules/FilterList/Item";
import FilterListWrapper from "@client/components/modules/FilterList/Wrapper";
import ListTemplate from "@client/components/templates/ListTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import ExportPdfButton from "@client/utils/ExportPdfButton";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import partnerImage from "@client/components/ui/sharedImages/partner.svg";
import partnerStore from "@client/stores/PartnerStore";
import Style from "@client/utils/Style";
import { Partner } from "@typings/Partner";

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

const ExportButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
`;

type State = {
  showPartnerModal: boolean;
  selectedPartners: Partner[];
  showDeleteAlert: boolean;
  showDeleteErrorAlert: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
};

@observer
class PartnersPage extends React.Component<Props, State> {
  state: State = {
    showPartnerModal: false,
    selectedPartners: [],
    showDeleteAlert: false,
    showDeleteErrorAlert: false,
  };

  componentDidMount() {
    partnerStore.load();
  }

  handleItemClick(partner: Partner) {
    this.props.onNavigate(`/partners/${partner.id}`);
  }

  handleReloadButtonClick() {
    partnerStore.load({ forceShowLoading: true });
  }

  itemFilterFunction(partner: Partner, _?: string | null, filterText?: string) {
    filterText = (filterText || "").toLowerCase();
    if (
      partner.id.toString().indexOf(filterText) === -1 &&
      partner.name.toLowerCase().indexOf(filterText) === -1 &&
      (!partner.description ||
        partner.description.toLowerCase().indexOf(filterText) === -1)
    ) {
      return false;
    }

    return true;
  }

  getListColumns(partner: Partner) {
    return [
      {
        name: "PoC per Customer",
        value: partner.poc_licences,
        style: { minWidth: "144px", maxWidth: "144px" },
      },
      {
        name: "Trials per Customer",
        value: partner.trial_licences,
        style: { minWidth: "144px", maxWidth: "144px" },
      },
      {
        name: "Created at",
        value: moment(partner.created_at).format("DD MMM YYYY HH:mm"),
        style: { minWidth: "144px", maxWidth: "144px" },
      },
    ];
  }

  handleDeleteBulkAction() {
    this.setState({ showDeleteAlert: true });
  }

  async handleDeleteBulkPartners() {
    this.setState({ showDeleteAlert: false });
    this.state.selectedPartners.forEach(partner => {
      partnerStore.delete(partner.id);
    });
  }

  render() {
    const BulkActions: DropdownActionType[] = [
      {
        label: "Delete Partners",
        color: Style.palette.alert,
        action: () => {
          this.handleDeleteBulkAction();
        },
      },
    ];

    const partners = partnerStore.partners;

    const columns = [
      { header: "No.", key: "index" },
      { header: "Name", key: "name" },
      { header: "Description", key: "description" },
      { header: "PoC per Customer", key: "poc_licences" },
      { header: "Trials per Customer", key: "trial_licences" },
      { header: "Created at", key: "created_at" },
    ];

    const dataWithIndex = partners.map((partner, index) => ({
      ...partner,
      index: index + 1,
      created_at: moment(partner.created_at).format("DD MMM YYYY HH:mm"),
    }));

    return (
      <Wrapper>
        <ListTemplate
          title="Coriolis Partners"
          listComponent={
            <FilterListWrapper
              filterItems={[{ label: "All", value: "all" }]}
              dropdownActions={BulkActions}
              onSelectedItemsChange={selectedPartners => {
                this.setState({ selectedPartners: selectedPartners });
              }}
              selectionLabel="partners"
              loading={partnerStore.loading}
              items={partners}
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              itemFilterFunction={(...args) => this.itemFilterFunction(...args)}
              renderItemComponent={options => {
                const partner: Partner = options.item;
                return (
                  <FilterListItem
                    {...options}
                    image={partnerImage}
                    title={partner.name}
                    subtitle={partner.description}
                    columns={this.getListColumns(options.item)}
                    selectionEnabled
                  />
                );
              }}
              emptyListImage={partnerImage}
              emptyListMessage="You don't have any created partners"
              emptyListExtraMessage="The created partners will appear here"
              emptyListButtonLabel="New Partner"
              onEmptyListButtonClick={() => {
                this.setState({ showPartnerModal: true });
              }}
            />
          }
          footerText={partners.length ? `${partners.length} partners` : ""}
        />
        <ExportButtonWrapper>
          <ExportPdfButton
            data={dataWithIndex}
            title="Partners"
            columns={columns}
          />
        </ExportButtonWrapper>
        {this.state.showPartnerModal ? (
          <PartnerModal
            onRequestClose={() => {
              this.setState({ showPartnerModal: false });
            }}
          />
        ) : null}
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Partners?"
            message="Are you sure you want to delete the selected partners?"
            extraMessage="Deleting a Coriolis Partner is permanent!"
            onConfirmation={() => {
              this.handleDeleteBulkPartners();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showDeleteErrorAlert ? (
          <AlertModal
            isOpen
            type="error"
            title="Cannot Delete Partners"
            message="Some of the selected partners have licences associated with them"
            extraMessage="You must first delete the associated licences"
            onRequestClose={() => {
              this.setState({ showDeleteErrorAlert: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function PartnersPageWithNavigate() {
  const navigate = useNavigate();
  return <PartnersPage onNavigate={navigate} />;
}

export default PartnersPageWithNavigate;
